import { useState, useRef, useEffect, useCallback, forwardRef, useImperativeHandle } from 'react';
import { message } from 'antd';
import { streamChatMessageV2 } from "../../../../api/chat";
import logger from '../../../../utils/logger';
import { analytics } from '../../../../firebaseConfig';
import { logEvent } from 'firebase/analytics';
import { getSourceByCategory } from '../utils/chatUtils';

/**
 * StreamingChat component for handling streaming chat functionality
 */
const StreamingChat = forwardRef(({
    auth,
    selectedChatId,
    selectedInstrument,
    selectedCategory,
    selectedModel,
    onMessageUpdate,
    onStreamingStatusChange,
    onStreamingComplete,
    onStreamingError,
    onThinkingUpdate,
    onToolUpdate
}, ref) => {
    //eslint-disable-next-line
    const [streamingResponse, setStreamingResponse] = useState("");
    const closeStreamingConnectionRef = useRef(null);
    const processingMessageRef = useRef(null);
    const isComponentMounted = useRef(true);

    // Track thinking state
    const thinkingStateRef = useRef({
        isThinking: false,
        thinkingId: null,
        content: '',
        startTime: null
    });

    // Track tool executions
    const toolsStateRef = useRef({});

    // Function to properly clean up streaming connections
    const cleanupStreamingConnection = useCallback(() => {
        if (closeStreamingConnectionRef.current && typeof closeStreamingConnectionRef.current === 'function') {
            try {
                closeStreamingConnectionRef.current();
            } catch (err) {
                logger.error("Error closing streaming connection:", err);
            }
            closeStreamingConnectionRef.current = null;
        }

        // Mark any running tools as complete before resetting
        if (isComponentMounted.current && onToolUpdate) {
            Object.values(toolsStateRef.current).forEach(tool => {
                if (tool.status === 'running' || tool.status === 'pending') {
                    const durationMs = tool.startTime
                        ? Date.now() - tool.startTime
                        : 0;
                    onToolUpdate({
                        type: 'end',
                        toolId: tool.toolId,
                        toolName: tool.toolName,
                        status: 'success', // Default to success if no explicit error
                        arguments: tool.arguments,
                        result: tool.result,
                        durationMs
                    });
                }
            });
        }

        // Reset thinking state
        thinkingStateRef.current = {
            isThinking: false,
            thinkingId: null,
            content: '',
            startTime: null
        };

        // Reset tool states
        toolsStateRef.current = {};

        // Reset streaming status only if component is still mounted
        if (isComponentMounted.current) {
            if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                onStreamingStatusChange("disconnected");
            }
            // Also update any message that might still be marked as streaming
            onMessageUpdate(prev => {
                const newMessages = [...prev];
                if (newMessages.length > 0 && (newMessages[newMessages.length - 1].isStreaming || newMessages[newMessages.length - 1].isProcessing)) {
                    newMessages[newMessages.length - 1] = {
                        ...newMessages[newMessages.length - 1],
                        isStreaming: false,
                        isProcessing: false
                    };
                }
                return newMessages;
            });
        }
    }, [onMessageUpdate, onStreamingStatusChange, onToolUpdate]);

    // Clean up streaming connection when component unmounts or chat changes
    useEffect(() => {
        isComponentMounted.current = true;
        
        return () => {
            isComponentMounted.current = false;
            cleanupStreamingConnection();
        };
    }, [selectedChatId, cleanupStreamingConnection]);

    /**
     * Send a message using streaming mode
     * @param {string} text - The message text to send
     * @param {number} messageStartTime - The timestamp when the message was sent
     */
    const sendStreamingMessage = useCallback((text, messageStartTime) => {
        if (!selectedChatId) {
            message.warning('Please select an instrument first');
            return;
        }
        
        if (text) {
            // Log message sending attempt
            logEvent(analytics, 'chat_message_sent', {
                chat_id: selectedChatId,
                message_length: text.length,
                instrument: selectedInstrument,
                category: selectedCategory,
                streaming_mode: true
            });

            let metadata = {
                symbol: selectedInstrument,
                timestamp: Date.now(),
                category: selectedCategory,
                model: selectedModel,
                source: getSourceByCategory(selectedCategory)
            };

            // Close any existing streaming connection
            cleanupStreamingConnection();

            // Reset streaming response
            setStreamingResponse("");
            if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                onStreamingStatusChange("connecting");
            }
            
            // Add a placeholder message for the streaming response
            onMessageUpdate(prev => [
                ...prev,
                { 
                    text: "", 
                    sender: "assistant",
                    timestamp: new Date().toISOString(),
                    model: selectedModel,
                    isStreaming: true
                }
            ]);

            // Start streaming
            closeStreamingConnectionRef.current = streamChatMessageV2(
                auth,
                // onMessage
                (data) => {
                    if (data.content) {
                        if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                            onStreamingStatusChange("connected");
                        }
                        setStreamingResponse(prev => prev + data.content);

                        // Update the last message with the current streaming response
                        onMessageUpdate(prev => {
                            const newMessages = [...prev];
                            if (newMessages.length > 0) {
                                // Check if this is the first content chunk
                                const isFirstChunk = prev[prev.length - 1].isProcessing || prev[prev.length - 1].text === "";

                                newMessages[newMessages.length - 1] = {
                                    ...newMessages[newMessages.length - 1],
                                    // If it's the first chunk, replace the text, otherwise append
                                    text: isFirstChunk ? data.content : prev[prev.length - 1].text + data.content,
                                    isStreaming: true,
                                    isProcessing: false // Remove the processing flag
                                };
                            }
                            return newMessages;
                        });
                    }
                },
                // onError
                (error) => {
                    logger.error("Streaming error:", error);
                    message.error(error.error || "Error in streaming response");
                    
                    // Set isStreaming to false on error
                    onMessageUpdate(prev => {
                        const newMessages = [...prev];
                        if (newMessages.length > 0) {
                            newMessages[newMessages.length - 1] = {
                                ...newMessages[newMessages.length - 1],
                                isStreaming: false,
                                isProcessing: false
                            };
                        }
                        return newMessages;
                    });
                    
                    if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                        onStreamingStatusChange("disconnected");
                    }
                    onStreamingError(error);
                    
                    // Ensure connection is cleaned up on error
                    cleanupStreamingConnection();
                },
                // onComplete
                (data) => {
                    // Log successful message send
                    logEvent(analytics, 'chat_message_success', {
                        chat_id: selectedChatId,
                        message_length: text.length,
                        response_time: Date.now() - messageStartTime,
                        instrument: selectedInstrument,
                        category: selectedCategory,
                        streaming_mode: true
                    });

                    // Set isStreaming to false after completion so cursor stops flashing
                    onMessageUpdate(prev => {
                        const newMessages = [...prev];
                        if (newMessages.length > 0) {
                            // Ensure the isStreaming flag is set to false
                            newMessages[newMessages.length - 1] = {
                                ...newMessages[newMessages.length - 1],
                                isStreaming: false,
                                isProcessing: false
                            };
                        }
                        return newMessages;
                    });

                    // Add metadata if available
                    if (data.metadata && data.metadata.lines) {
                        onMessageUpdate(prev => [
                            ...prev,
                            { 
                                text: JSON.stringify({ lines: data.metadata.lines }), 
                                sender: "processing",
                                timestamp: new Date().toISOString(),
                                model: selectedModel
                            }
                        ]);    
                    }
                    
                    if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                        onStreamingStatusChange("disconnected");
                    }
                    onStreamingComplete(data);
                    
                    // Ensure connection is properly cleaned up
                    cleanupStreamingConnection();
                },
                // onLimit
                (data) => {
                    message.warning(data.message || "Daily message limit reached");
                    
                    // Set isStreaming to false when limit is reached
                    onMessageUpdate(prev => {
                        const newMessages = [...prev];
                        if (newMessages.length > 0) {
                            newMessages[newMessages.length - 1] = {
                                ...newMessages[newMessages.length - 1],
                                isStreaming: false,
                                isProcessing: false
                            };
                        }
                        return newMessages;
                    });
                    
                    if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                        onStreamingStatusChange("disconnected");
                    }
                    onStreamingError(data);
                    
                    // Ensure connection is cleaned up on limit reached
                    cleanupStreamingConnection();
                },
                // onProcessing
                (data) => {
                    if (onStreamingStatusChange && typeof onStreamingStatusChange === 'function') {
                        onStreamingStatusChange("waiting");
                    }
                    
                    // Create a processing message with gray styling
                    const processingMessage = {
                        text: data.status || "Processing your message...",
                        sender: "assistant",
                        timestamp: new Date().toISOString(),
                        model: selectedModel,
                        isProcessing: true,
                        isStreaming: true,
                        step: data.step || "unknown"
                    };
                    
                    // Store the processing message reference
                    processingMessageRef.current = processingMessage;
                    
                    // Update the last message with a processing indicator
                    onMessageUpdate(prev => {
                        const newMessages = [...prev];
                        if (newMessages.length > 0) {
                            newMessages[newMessages.length - 1] = processingMessage;
                        }
                        return newMessages;
                    });
                },
                selectedChatId,
                text,
                null,
                metadata,
                selectedModel,
                // onThinkingStart
                (data) => {
                    thinkingStateRef.current = {
                        isThinking: true,
                        thinkingId: data.thinking_id,
                        content: '',
                        startTime: Date.now()
                    };
                    if (onThinkingUpdate) {
                        onThinkingUpdate({
                            type: 'start',
                            thinkingId: data.thinking_id,
                            isThinking: true,
                            content: ''
                        });
                    }
                },
                // onThinkingDelta
                (data) => {
                    thinkingStateRef.current.content += data.content;
                    if (onThinkingUpdate) {
                        onThinkingUpdate({
                            type: 'delta',
                            thinkingId: data.thinking_id,
                            isThinking: true,
                            content: thinkingStateRef.current.content
                        });
                    }
                },
                // onThinkingEnd
                (data) => {
                    const durationMs = thinkingStateRef.current.startTime
                        ? Date.now() - thinkingStateRef.current.startTime
                        : data.duration_ms || 0;

                    if (onThinkingUpdate) {
                        onThinkingUpdate({
                            type: 'end',
                            thinkingId: data.thinking_id,
                            isThinking: false,
                            content: thinkingStateRef.current.content,
                            durationMs,
                            summary: data.summary
                        });
                    }

                    thinkingStateRef.current = {
                        isThinking: false,
                        thinkingId: null,
                        content: '',
                        startTime: null
                    };
                },
                // onToolStart
                (data) => {
                    toolsStateRef.current[data.tool_id] = {
                        toolId: data.tool_id,
                        toolName: data.tool_name,
                        status: 'running',
                        arguments: data.arguments,
                        startTime: Date.now()
                    };
                    if (onToolUpdate) {
                        onToolUpdate({
                            type: 'start',
                            ...toolsStateRef.current[data.tool_id]
                        });
                    }
                },
                // onToolEnd
                (data) => {
                    const toolState = toolsStateRef.current[data.tool_id] || {};
                    const durationMs = toolState.startTime
                        ? Date.now() - toolState.startTime
                        : data.duration_ms || 0;

                    // Ensure status is valid - default to 'success' if not provided or invalid
                    const finalStatus = data.status && ['success', 'error'].includes(data.status)
                        ? data.status
                        : 'success';

                    toolsStateRef.current[data.tool_id] = {
                        ...toolState,
                        toolId: data.tool_id,
                        toolName: data.tool_name || toolState.toolName,
                        status: finalStatus,
                        arguments: data.arguments || toolState.arguments,
                        result: data.result,
                        durationMs
                    };

                    if (onToolUpdate) {
                        onToolUpdate({
                            type: 'end',
                            ...toolsStateRef.current[data.tool_id]
                        });
                    }
                }
            );
        }
    }, [
        auth,
        selectedChatId,
        selectedInstrument,
        selectedCategory,
        selectedModel,
        onMessageUpdate,
        onStreamingStatusChange,
        onStreamingComplete,
        onStreamingError,
        onThinkingUpdate,
        onToolUpdate,
        cleanupStreamingConnection
    ]);

    // Expose the sendStreamingMessage method via ref
    useImperativeHandle(ref, () => ({
        sendStreamingMessage,
        cleanupStreamingConnection
    }));

    // This component doesn't render anything
    return null;
});

StreamingChat.displayName = 'StreamingChat';

export default StreamingChat; 